import { useState } from 'react';
import { useTranslation } from '../../i18n';

const METRICS = [
  { nameKey: 'postingFrequency', grade: 'A', colour: 'sage' },
  { nameKey: 'topicCoverage',    grade: 'B', colour: 'gold' },
  { nameKey: 'platformActivity', grade: 'A', colour: 'sage' },
  { nameKey: 'engagementTrend',  grade: 'C', colour: 'orange' },
  { nameKey: 'consistency',      grade: 'B', colour: 'gold' },
];

// TODO(api): replace with GET /api/v1/drafts?status=published.
const MOCK_DRAFTS = [
  { id: 'd1', label: 'Heritage Summit Kraków · LinkedIn pre-event' },
  { id: 'd2', label: 'Heritage Summit Kraków · Instagram recap' },
  { id: 'd3', label: 'Youth Workshop Warsaw · LinkedIn announcement' },
  { id: 'd4', label: 'UNESCO Partner Meeting · Facebook recap' },
];

// TODO(api): replace with GET /api/v1/usage.
const USAGE = {
  used: 658,
  total: 900,
  resetIn: '4h 12m',
  byType: [
    { key: 'caption',       count: 234 },
    { key: 'imageAnalysis', count: 156 },
    { key: 'briefing',      count: 98 },
    { key: 'stories',       count: 78 },
    { key: 'campaign',      count: 64 },
    { key: 'ocr',           count: 28 },
  ],
  byUser: [
    { name: 'Rushil', percent: 63 },
    { name: 'Anna',   percent: 24 },
    { name: 'Tomek',  percent: 11 },
    { name: 'Maria',  percent: 3  },
  ],
};

function usageFillVariant(percent) {
  if (percent >= 90) return 'usage-bar__fill--danger';
  if (percent >= 75) return 'usage-bar__fill--warning';
  return '';
}

export function AnalyticsPage() {
  const { t } = useTranslation();
  const [draftId, setDraftId] = useState('');
  const [perf, setPerf] = useState({ likes: '', comments: '', shares: '', reach: '' });

  const usagePercent = Math.round((USAGE.used / USAGE.total) * 100);
  const remaining = USAGE.total - USAGE.used;
  const maxByType = Math.max(...USAGE.byType.map((b) => b.count));

  return (
    <div>
      <header className="page-header">
        <h1 className="page-header__title">{t('analytics.title')}</h1>
        <div className="page-header__subtitle">{t('analytics.subtitle')}</div>
      </header>

      <section className="metric-grid" style={{ marginBottom: 'var(--space-6)' }}>
        {METRICS.map((m) => (
          <div key={m.nameKey} className="metric-card">
            <div className={`metric-card__grade metric-card__grade--${m.colour}`}>{m.grade}</div>
            <div className="metric-card__name">{t(`analytics.metrics.${m.nameKey}`)}</div>
          </div>
        ))}
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }} className="analytics-row">
        <section className="perf-form">
          <div>
            <h2 className="usage-section__title">{t('analytics.logPerformance.title')}</h2>
            <div className="page-header__subtitle">{t('analytics.logPerformance.subtitle')}</div>
          </div>
          <label className="form-field">
            <span className="form-field__label">{t('analytics.logPerformance.draftLabel')}</span>
            <select
              className="form-field__select"
              value={draftId}
              onChange={(e) => setDraftId(e.target.value)}
            >
              <option value="">{t('analytics.logPerformance.draftPlaceholder')}</option>
              {MOCK_DRAFTS.map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
          </label>
          <div className="perf-form__row">
            {['likes', 'comments', 'shares', 'reach'].map((field) => (
              <label key={field} className="form-field">
                <span className="form-field__label">{t(`analytics.logPerformance.${field}`)}</span>
                <input
                  type="number"
                  min="0"
                  className="form-field__input"
                  value={perf[field]}
                  onChange={(e) => setPerf((prev) => ({ ...prev, [field]: e.target.value }))}
                />
              </label>
            ))}
          </div>
          <button type="button" className="button button--primary" disabled={!draftId} style={{ alignSelf: 'flex-start' }}>
            {t('analytics.logPerformance.save')}
          </button>
        </section>

        <section className="usage-section">
          <h2 className="usage-section__title">{t('analytics.aiUsage.title')}</h2>
          <div className="usage-summary">
            <span className="usage-summary__big">{usagePercent}%</span>
            <span className="usage-summary__small">
              · {t('analytics.aiUsage.summary', { used: USAGE.used, total: USAGE.total })}
            </span>
            <span className="usage-summary__reset">
              {t('analytics.aiUsage.resetsIn', { time: USAGE.resetIn })}
            </span>
          </div>
          <div className="usage-bar">
            <div
              className={`usage-bar__fill ${usageFillVariant(usagePercent)}`}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
          <div className="page-header__subtitle" style={{ marginTop: 'var(--space-2)' }}>
            {t('analytics.aiUsage.remaining', { count: remaining })}
          </div>

          <div className="usage-breakdown">
            <div className="usage-breakdown__col">
              <h4>{t('analytics.aiUsage.byTypeTitle')}</h4>
              {USAGE.byType.map((row) => (
                <div key={row.key} className="usage-row">
                  <div className="usage-row__label">
                    <span>{t(`analytics.aiUsage.types.${row.key}`)}</span>
                    <span className="usage-row__value">{row.count}</span>
                  </div>
                  <div className="usage-row__bar">
                    <div
                      className="usage-row__bar-fill"
                      style={{ width: `${(row.count / maxByType) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="usage-breakdown__col">
              <h4>{t('analytics.aiUsage.byUserTitle')}</h4>
              {USAGE.byUser.map((u) => (
                <div key={u.name} className="usage-row">
                  <div className="usage-row__label">
                    <span>{u.name}</span>
                    <span className="usage-row__value">{u.percent}%</span>
                  </div>
                  <div className="usage-row__bar">
                    <div className="usage-row__bar-fill" style={{ width: `${u.percent}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
